import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import type { SvgIconComponent } from '@mui/icons-material';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import InsightsIcon from '@mui/icons-material/Insights';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import PercentIcon from '@mui/icons-material/Percent';
import ScienceIcon from '@mui/icons-material/Science';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { DOCS_URL, TESTNET_URL } from '@/utils/externalLinks';
import type { NavItem } from '../constants';

export interface ToolsMenuItemMeta {
  Icon: SvgIconComponent;
  /** Undefined for routes without an entry here — the panel falls back to the raw path. */
  description?: MessageDescriptor;
}

const TOOLS_ITEM_META: Record<string, ToolsMenuItemMeta> = {
  '/dashboard': {
    Icon: InsightsIcon,
    description: msg({ comment: 'Tools menu dashboard description', message: 'Protocol volume, TVL and onchain stats' }),
  },
  '/leaderboard': {
    Icon: FormatListNumberedIcon,
    description: msg({ comment: 'Tools menu leaderboard description', message: 'Top traders on Perpl' }),
  },
  '/funding-rates': {
    Icon: PercentIcon,
    description: msg({ comment: 'Tools menu funding rates description', message: 'Live funding rates across markets' }),
  },
  '/builders': {
    Icon: IntegrationInstructionsIcon,
    description: msg({ comment: 'Tools menu builder codes description', message: 'Apps and bots built on Perpl' }),
  },
  '/apikeys': {
    Icon: VpnKeyIcon,
    description: msg({ comment: 'Tools menu API keys description', message: 'Manage exchange API keys' }),
  },
  '/changelog': {
    Icon: NewReleasesIcon,
    description: msg({ comment: 'Tools menu changelog description', message: 'Latest releases and fixes' }),
  },
  [DOCS_URL]: {
    Icon: MenuBookIcon,
    description: msg({ comment: 'Tools menu docs description', message: 'Guides and API reference' }),
  },
  [TESTNET_URL]: {
    Icon: ScienceIcon,
    description: msg({ comment: 'Tools menu testnet description', message: 'Trade on Perpl testnet' }),
  },
};

const FALLBACK_META: ToolsMenuItemMeta = {
  Icon: VpnKeyIcon,
};

export function getToolsItemMeta(item: Pick<NavItem, 'path'>): ToolsMenuItemMeta {
  return TOOLS_ITEM_META[item.path] ?? FALLBACK_META;
}
